import "./ProductOrders.scss";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ShowUser from "./../../assets/show-user.svg";
import { ConfigProvider, DatePicker } from "antd";
import { DataGrid } from "@mui/x-data-grid";
import { ProductOrdersColumns } from "../../SourceData";
import { Link, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import ar_EG from "antd/es/locale/ar_EG"; // Arabic locale
import "dayjs/locale/ar"; // Import Arabic locale for dayjs
import { usePermissions } from "./../../hooks/usePermissions";
import { toast } from "react-toastify";

const { RangePicker } = DatePicker;

const ProductOrders = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const navigate = useNavigate();
  // State variables
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(10); // Default value: "الجميع"
  const [selectedStatus, setSelectedStatus] = useState(10); // Default value: "الجميع"

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Services data state
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  // Add state for new filters
  const [hasDiscount, setHasDiscount] = useState(false);
  const [isPackage, setIsPackage] = useState(false);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1); // Reset to first page when filters change
  };

  // Update the fetchServices function to include date range parameters
  const fetchServices = async () => {
    if (!hasPermission(63)) {
      return;
    }
    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.q = searchTerm;
        // params.local = "ar";
      }

      // Add category filter if not "الجميع" (All)
      if (selectedCategory !== 10) {
        params.category = selectedCategory;
      }
      // Add category filter if not "الجميع" (All)
      if (selectedStatus !== 10) {
        params.status = selectedStatus;
      }

      // Add date range parameters if selected
      if (dateRange && dateRange[0] && dateRange[1]) {
        // Format dates as YYYY-MM-DD
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      // Add hasDiscount filter if checked
      if (hasDiscount) {
        params.hasDiscount = "true";
      }

      // Add completed status filter if checked
      if (isPackage) {
        params.isPackage = true;
      }

      const response = await api.get("/bookings", { params });

      if (response.data.status === "success") {
        setServices(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!hasPermission(63)) {
      return;
    }
    try {
      const response = await api.get("/categories", {
        params: {
          limit: 100, // Get a large number to show all categories in dropdown
        },
      });

      if (response.data.status === "success") {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Effect to fetch services when dependencies change
  useEffect(() => {
    fetchServices();
  }, [
    page,
    pageSize,
    searchTerm,
    selectedCategory,
    dateRange,
    hasDiscount,
    isPackage,
    selectedStatus,
  ]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const getDirections = (destination) => {
    const [lng, lat] = destination.coordinates;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const origin = `${position.coords.latitude},${position.coords.longitude}`;
        const dest = `${lat},${lng}`;
        const url = `https://www.google.com/maps/dir/${origin}/${dest}`;
        window.open(url, "_blank");
      });
    } else {
      // Fallback to just showing destination
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(url, "_blank");
    }
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "",
      width: 160,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div
            className="cell-btn"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
            onClick={() => {
              if (hasPermission(64)) {
                navigate(`/orders/${params.row._id}`);
              } else {
                toast.error("ليس لديك صلاحية لعرض التفاصيل");
              }
            }}
          >
            <img src={ShowUser} alt="عرض" />
          </div>
        );
      },
    },
  ];

  return (
    <div className="products">
      <div className="special-header">
        <p className="title">
          الطلبات <span className="count-badge">{totalCount || 0}</span>
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث باسم العميل او رقم الجوال...."
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <ConfigProvider locale={ar_EG} direction="rtl">
              <RangePicker
                format={"DD MMM"}
                style={{
                  width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                  padding: "0px 10px",
                }}
                placeholder={["من", "الي"]}
                className="range-picker"
                onChange={handleDateRangeChange}
                value={dateRange}
                popupStyle={{ direction: "rtl" }}
                inputReadOnly={true}
              />
            </ConfigProvider>
          </div>
        </div>

        <div className="filters-container">
          <div className="filters">
            <FormControl
              sx={{
                width: isMobile ? "100%" : "180px",
              }}
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                الاقسام
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
                displayEmpty={true}
                sx={{ borderRadius: "8px", height: "48px" }}
              >
                <MenuItem value={10}>الجميع</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category?.name?.ar}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              sx={{
                width: isMobile ? "100%" : "180px",
              }}
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                حالة الطلب
              </InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                displayEmpty={true}
                sx={{ borderRadius: "8px", height: "48px" }}
              >
                <MenuItem value={10}>الجميع</MenuItem>
                <MenuItem value="completed">المكتملة</MenuItem>
                <MenuItem value="canceled">الملغية</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* <div className="excels-actions">
            <img src={Download} alt="" />
          </div> */}
        </div>
        <div className="checked-container">
          <div className="item">
            <input
              type="checkbox"
              checked={hasDiscount}
              onChange={(e) => setHasDiscount(e.target.checked)}
            />
            <label>الطلبات التي تحتوي على خصم</label>
          </div>
          <div className="item">
            <input
              type="checkbox"
              checked={isPackage}
              onChange={(e) => setIsPackage(e.target.checked)}
            />
            <label>طلبات المشتركين</label>
          </div>
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={ProductOrdersColumns.concat(actionColumn)}
          rows={services.map((service, index) => ({
            ...service,
            // Generate unique id if not present
            id: service._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "طلب في الصفحة",
            },
          }}
        />
      </div>
    </div>
  );
};

export default ProductOrders;
